import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { FaArrowRight, FaSearch, FaUser, FaTimes, FaChevronDown } from "react-icons/fa";
import { MdOutlineAddCard } from "react-icons/md";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Cookies from "js-cookie";

/** ✅ helper to avoid double slashes (common cause of 404s on some servers) */
const joinUrl = (base, path) => {
  const b = String(base || "").replace(/\/+$/, "");
  const p = String(path || "").replace(/^\/+/, "");
  return `${b}/${p}`;
};

// ─── Step Indicator ───────────────────────────────────────────────────────────
const StepIndicator = ({ currentStep, steps }) => (
  <div className="flex items-center justify-center mb-8">
    {steps.map((step, index) => {
      const stepNo = index + 1;
      const isCompleted = currentStep > stepNo;
      const isActive = currentStep === stepNo;

      return (
        <React.Fragment key={stepNo}>
          <div className="flex flex-col items-center">
            <div
              className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-sm transition-all duration-300 shadow-md
                ${isCompleted ? "bg-green-500 text-white" : ""}
                ${isActive ? "bg-blue-600 text-white ring-4 ring-blue-200" : ""}
                ${!isCompleted && !isActive ? "bg-gray-200 text-gray-500" : ""}
              `}
            >
              {isCompleted ? (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              ) : (
                stepNo
              )}
            </div>
            <span
              className={`mt-2 text-xs font-medium ${
                isActive ? "text-blue-600" : isCompleted ? "text-green-500" : "text-gray-400"
              }`}
            >
              {step}
            </span>
          </div>

          {index < steps.length - 1 && (
            <div
              className={`flex-1 h-1 mx-3 rounded transition-all duration-500 ${
                isCompleted ? "bg-green-400" : "bg-gray-200"
              }`}
              style={{ maxWidth: "80px" }}
            />
          )}
        </React.Fragment>
      );
    })}
  </div>
);

// ─── Reusable Field Components ────────────────────────────────────────────────
const InputField = ({
  label,
  name,
  value,
  onChange,
  type = "text",
  error,
  required = false,
  placeholder = "",
}) => (
  <div>
    <label className="block text-sm text-gray-700 mb-1">
      {label}
      {required && <span className="text-red-500 ml-1">*</span>}
    </label>
    <input
      name={name}
      value={value}
      onChange={onChange}
      type={type}
      placeholder={placeholder}
      className={`w-full border ${error ? "border-red-500 bg-red-50" : "border-gray-300"} p-2 rounded focus:outline-none focus:ring-2 focus:ring-blue-300 transition`}
    />
    {error && <p className="text-red-500 text-sm mt-1">{error}</p>}
  </div>
);

// ─── Employee Search Dropdown ────────────────────────────────────────────────
const EmployeeSearchDropdown = ({ value, onSelect, error, required = false }) => {
  const API_URL = process.env.REACT_APP_FRONTEND_URL;
  const token = Cookies.get("user_token");

  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState(null);

  const dropdownRef = useRef(null);
  const debounceRef = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const fetchEmployees = useCallback(
    async (search) => {
      setLoading(true);
      try {
        const params = new URLSearchParams();
        params.set("page", "1");
        params.set("limit", "20");
        if (search?.trim()) params.set("search", search.trim());

        const url = joinUrl(API_URL, `v1/hris/employees/employee/all-details?${params.toString()}`);

        const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });

        const result = await res.json().catch(() => ({}));

        if (!res.ok || !result.success) {
          setResults([]);
          return;
        }

        setResults(Array.isArray(result.data) ? result.data : []);
      } catch (err) {
        console.error("Employee fetch error:", err);
        setResults([]);
      } finally {
        setLoading(false);
      }
    },
    [API_URL, token]
  );

  const handleOpen = () => {
    setOpen((prev) => !prev);
    if (!open && results.length === 0 && !selected) fetchEmployees("");
  };

  const handleSelect = (emp) => {
    setSelected(emp);
    setQuery("");
    setOpen(false);
    onSelect(emp.employee_no, emp);
  };

  const handleClear = () => {
    setSelected(null);
    setQuery("");
    setResults([]);
    onSelect("", null);
  };

  const handleSearchChange = (e) => {
    const val = e.target.value;
    setQuery(val);

    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      fetchEmployees(val);
    }, 350);
  };

  return (
    <div>
      <label className="block text-sm text-gray-700 mb-1">
        Employee
        {required && <span className="text-red-500 ml-1">*</span>}
      </label>

      <div ref={dropdownRef} className="relative">
        {selected ? (
          <div className="flex items-center gap-3 border-2 rounded-lg px-3 py-2.5 bg-blue-50 border-blue-300 transition-all">
            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center flex-shrink-0 text-white text-xs font-bold">
              {(selected.employee_calling_name || selected.employee_fullname || "?")[0]?.toUpperCase?.() || "?"}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-gray-800 truncate">
                {selected.employee_calling_name || selected.employee_fullname}
                <span className="ml-2 text-xs font-mono text-blue-600 bg-blue-100 px-1.5 py-0.5 rounded">
                  {selected.employee_no}
                </span>
              </p>
              <p className="text-xs text-gray-500 truncate">
                {selected.designation_name} · {selected.department_name}
              </p>
            </div>
            <button
              type="button"
              onClick={handleClear}
              className="flex-shrink-0 w-6 h-6 rounded-full bg-gray-200 hover:bg-red-100 hover:text-red-600 flex items-center justify-center text-gray-500 transition-colors"
              title="Change employee"
            >
              <FaTimes className="text-xs" />
            </button>
          </div>
        ) : (
          <button
            type="button"
            onClick={handleOpen}
            className={`w-full flex items-center justify-between border ${
              error ? "border-red-500 bg-red-50" : "border-gray-300 bg-white"
            } px-3 py-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-300 transition text-sm`}
          >
            <div className="flex items-center gap-2 text-gray-600">
              <FaUser className="text-gray-400 text-sm" />
              <span>Select employee</span>
            </div>
            <div className="flex items-center gap-2">
              {loading ? (
                <svg className="animate-spin h-4 w-4 text-blue-400" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                </svg>
              ) : (
                <FaChevronDown className="text-gray-400 text-xs" />
              )}
            </div>
          </button>
        )}

        <AnimatePresence>
          {open && !selected && (
            <motion.div
              initial={{ opacity: 0, y: -6, scaleY: 0.95 }}
              animate={{ opacity: 1, y: 0, scaleY: 1 }}
              exit={{ opacity: 0, y: -6, scaleY: 0.95 }}
              transition={{ duration: 0.15 }}
              className="absolute z-50 top-full mt-1 w-full bg-white border border-gray-200 rounded-xl shadow-xl overflow-hidden"
              style={{ transformOrigin: "top" }}
            >
              <div className="p-3 border-b border-gray-100 bg-white">
                <div className="relative">
                  <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm pointer-events-none" />
                  <input
                    type="text"
                    value={query}
                    onChange={handleSearchChange}
                    placeholder="Search by name, employee no, NIC…"
                    className="w-full border border-gray-300 pl-9 pr-3 py-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-300 transition text-sm"
                  />
                </div>
              </div>

              {loading && results.length === 0 ? (
                <div className="flex items-center gap-2 px-4 py-3 text-sm text-gray-400">
                  <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                  </svg>
                  Searching employees…
                </div>
              ) : results.length === 0 ? (
                <div className="px-4 py-3 text-sm text-gray-400 flex items-center gap-2">
                  <FaUser className="text-gray-300" />
                  No employees found
                </div>
              ) : (
                <div className="max-h-64 overflow-y-auto divide-y divide-gray-50">
                  {results.map((emp) => (
                    <button
                      key={emp.id}
                      type="button"
                      onClick={() => handleSelect(emp)}
                      className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-blue-50 transition-colors text-left group"
                    >
                      <div className="w-8 h-8 rounded-full bg-gradient-to-br from-slate-400 to-slate-600 group-hover:from-blue-500 group-hover:to-indigo-600 flex items-center justify-center flex-shrink-0 text-white text-xs font-bold transition-all">
                        {(emp.employee_calling_name || emp.employee_fullname || "?")[0]?.toUpperCase?.() || "?"}
                      </div>

                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-sm font-semibold text-gray-800 truncate">
                            {emp.employee_calling_name || emp.employee_fullname}
                          </span>
                          <span className="text-xs font-mono text-blue-600 bg-blue-50 px-1.5 py-0.5 rounded flex-shrink-0">
                            {emp.employee_no}
                          </span>
                          <span
                            className={`text-xs px-1.5 py-0.5 rounded-full font-medium flex-shrink-0 ${
                              emp.employee_active_status === "Active"
                                ? "bg-green-100 text-green-700"
                                : "bg-gray-100 text-gray-500"
                            }`}
                          >
                            {emp.employee_active_status}
                          </span>
                        </div>
                        <p className="text-xs text-gray-400 truncate mt-0.5">
                          {emp.designation_name} · {emp.department_name}
                          {emp.epf_no && <span className="ml-2 text-gray-300">EPF: {emp.epf_no}</span>}
                        </p>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {error && <p className="text-red-500 text-sm mt-1">{error}</p>}
    </div>
  );
};

// ─── Bank Search Dropdown ─────────────────────────────────────────────────────
const BankSearchDropdown = ({ value, onSelect, error, required = false }) => {
  const API_URL = process.env.REACT_APP_FRONTEND_URL;
  const token = Cookies.get("user_token");

  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState(null);

  const dropdownRef = useRef(null);
  const debounceRef = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const fetchBanks = useCallback(
    async (search) => {
      setLoading(true);
      try {
        const params = new URLSearchParams();
        params.set("page", "1");
        params.set("limit", "20");
        if (search?.trim()) params.set("search", search.trim());

        const url = joinUrl(API_URL, `v1/hris/bank?${params.toString()}`);

        const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
        const result = await res.json().catch(() => ({}));

        if (!res.ok || !result.success) {
          setResults([]);
          return [];
        }

        const list = Array.isArray(result.data) ? result.data : [];
        setResults(list);
        return list;
      } catch (err) {
        console.error("Bank fetch error:", err);
        setResults([]);
        return [];
      } finally {
        setLoading(false);
      }
    },
    [API_URL, token]
  );

  // ✅ IMPORTANT: Sync selected when parent "value" changes (edit mode / reset)
  useEffect(() => {
    if (!value) {
      setSelected(null);
      return;
    }

    // if we already have it in results, pick it
    const found = results.find((b) => String(b.id) === String(value));
    if (found) {
      setSelected(found);
      return;
    }

    // otherwise fetch and match (so UI shows selected even if list not loaded)
    (async () => {
      const list = results.length ? results : await fetchBanks("");
      const matched = list.find((b) => String(b.id) === String(value));
      if (matched) setSelected(matched);
    })();
  }, [value, results, fetchBanks]);

  const handleOpen = () => {
    setOpen((prev) => !prev);
    if (!open && results.length === 0 && !selected) fetchBanks("");
  };

  const handleSelect = (bank) => {
    setSelected(bank);
    setQuery("");
    setOpen(false);
    onSelect(String(bank.id), bank);
  };

  const handleClear = () => {
    setSelected(null);
    setQuery("");
    setResults([]);
    onSelect("", null);
  };

  const handleSearchChange = (e) => {
    const val = e.target.value;
    setQuery(val);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => fetchBanks(val), 350);
  };

  return (
    <div>
      <label className="block text-sm text-gray-700 mb-1">
        Bank Name{required && <span className="text-red-500 ml-1">*</span>}
      </label>

      <div ref={dropdownRef} className="relative">
        {selected ? (
          <div className="flex items-center gap-3 border-2 rounded-lg px-3 py-2.5 bg-blue-50 border-blue-300 transition-all">
            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center flex-shrink-0 text-white text-xs font-bold">
              {(selected.bank_name || "B")[0]?.toUpperCase?.() || "B"}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-gray-800 truncate">
                {selected.bank_name}
                {selected.bank_code && (
                  <span className="ml-2 text-xs font-mono text-blue-600 bg-blue-100 px-1.5 py-0.5 rounded">
                    {selected.bank_code}
                  </span>
                )}
              </p>
              <p className="text-xs text-gray-500 truncate">
                Bank ID: <span className="font-mono">{selected.id}</span>
              </p>
            </div>
            <button
              type="button"
              onClick={handleClear}
              className="flex-shrink-0 w-6 h-6 rounded-full bg-gray-200 hover:bg-red-100 hover:text-red-600 flex items-center justify-center text-gray-500 transition-colors"
              title="Change bank"
            >
              <FaTimes className="text-xs" />
            </button>
          </div>
        ) : (
          <button
            type="button"
            onClick={handleOpen}
            className={`w-full flex items-center justify-between border ${
              error ? "border-red-500 bg-red-50" : "border-gray-300 bg-white"
            } px-3 py-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-300 transition text-sm`}
          >
            <div className="flex items-center gap-2 text-gray-600">
              <FaUser className="text-gray-400 text-sm" />
              <span>Select bank</span>
            </div>
            <div className="flex items-center gap-2">
              {loading ? (
                <svg className="animate-spin h-4 w-4 text-blue-400" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                </svg>
              ) : (
                <FaChevronDown className="text-gray-400 text-xs" />
              )}
            </div>
          </button>
        )}

        <AnimatePresence>
          {open && !selected && (
            <motion.div
              initial={{ opacity: 0, y: -6, scaleY: 0.95 }}
              animate={{ opacity: 1, y: 0, scaleY: 1 }}
              exit={{ opacity: 0, y: -6, scaleY: 0.95 }}
              transition={{ duration: 0.15 }}
              className="absolute z-50 top-full mt-1 w-full bg-white border border-gray-200 rounded-xl shadow-xl overflow-hidden"
              style={{ transformOrigin: "top" }}
            >
              <div className="p-3 border-b border-gray-100 bg-white">
                <div className="relative">
                  <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm pointer-events-none" />
                  <input
                    type="text"
                    value={query}
                    onChange={handleSearchChange}
                    placeholder="Search bank name / code…"
                    className="w-full border border-gray-300 pl-9 pr-3 py-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-300 transition text-sm"
                  />
                </div>
              </div>

              {loading && results.length === 0 ? (
                <div className="flex items-center gap-2 px-4 py-3 text-sm text-gray-400">
                  <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                  </svg>
                  Loading banks…
                </div>
              ) : results.length === 0 ? (
                <div className="px-4 py-3 text-sm text-gray-400 flex items-center gap-2">
                  <FaUser className="text-gray-300" />
                  No banks found
                </div>
              ) : (
                <div className="max-h-64 overflow-y-auto divide-y divide-gray-50">
                  {results.map((bank) => (
                    <button
                      key={bank.id}
                      type="button"
                      onClick={() => handleSelect(bank)}
                      className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-blue-50 transition-colors text-left group"
                    >
                      <div className="w-8 h-8 rounded-full bg-gradient-to-br from-slate-400 to-slate-600 group-hover:from-blue-500 group-hover:to-indigo-600 flex items-center justify-center flex-shrink-0 text-white text-xs font-bold transition-all">
                        {(bank.bank_name || "B")[0]?.toUpperCase?.() || "B"}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-sm font-semibold text-gray-800 truncate">{bank.bank_name}</span>
                          {bank.bank_code && (
                            <span className="text-xs font-mono text-blue-600 bg-blue-50 px-1.5 py-0.5 rounded flex-shrink-0">
                              {bank.bank_code}
                            </span>
                          )}
                        </div>
                        <p className="text-xs text-gray-400 truncate mt-0.5">
                          Bank ID: <span className="font-mono">{bank.id}</span>
                        </p>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {error && <p className="text-red-500 text-sm mt-1">{error}</p>}
    </div>
  );
};

// ─── Branch Search Dropdown (depends on bankId) ────────────────────────────────
const BranchSearchDropdown = ({ bankId, value, onSelect, error, required = false, disabled = false }) => {
  const API_URL = process.env.REACT_APP_FRONTEND_URL;
  const token = Cookies.get("user_token");

  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState(null);

  const dropdownRef = useRef(null);
  const debounceRef = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // ✅ when bank changes: clear branch state + notify parent
  useEffect(() => {
    setSelected(null);
    setQuery("");
    setResults([]);
    setOpen(false);
    // Optional: force-clear parent branch when bank changes
    onSelect("", null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [String(bankId || "")]);

  const fetchBranches = useCallback(
    async (search) => {
      if (!bankId) return [];
      setLoading(true);
      try {
        const url = joinUrl(API_URL, `v1/hris/bank-branch/get-branches-by-bank/${bankId}`);

        const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
        const result = await res.json().catch(() => ({}));

        if (!res.ok || !result.success) {
          setResults([]);
          return [];
        }

        const data = Array.isArray(result.data) ? result.data : [];

        const filtered = !search?.trim()
          ? data
          : data.filter((b) => {
              const s = search.toLowerCase();
              return (
                (b.branch_name || "").toLowerCase().includes(s) ||
                (b.branch_code || "").toLowerCase().includes(s)
              );
            });

        const sliced = filtered.slice(0, 50);
        setResults(sliced);
        return sliced;
      } catch (err) {
        console.error("Branch fetch error:", err);
        setResults([]);
        return [];
      } finally {
        setLoading(false);
      }
    },
    [API_URL, token, bankId]
  );

  // ✅ IMPORTANT: Sync selected when parent "value" changes (edit mode / reset)
  useEffect(() => {
    if (!value || !bankId) {
      setSelected(null);
      return;
    }

    const found = results.find((b) => String(b.id) === String(value));
    if (found) {
      setSelected(found);
      return;
    }

    (async () => {
      const list = results.length ? results : await fetchBranches("");
      const matched = list.find((b) => String(b.id) === String(value));
      if (matched) setSelected(matched);
    })();
  }, [value, bankId, results, fetchBranches]);

  const handleOpen = () => {
    if (disabled || !bankId) return;
    setOpen((prev) => !prev);
    if (!open && results.length === 0 && !selected) fetchBranches("");
  };

  const handleSelect = (branch) => {
    setSelected(branch);
    setQuery("");
    setOpen(false);
    onSelect(String(branch.id), branch);
  };

  const handleClear = () => {
    setSelected(null);
    setQuery("");
    setResults([]);
    onSelect("", null);
  };

  const handleSearchChange = (e) => {
    const val = e.target.value;
    setQuery(val);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => fetchBranches(val), 250);
  };

  const isDisabled = disabled || !bankId;

  return (
    <div>
      <label className="block text-sm text-gray-700 mb-1">
        Branch Name{required && <span className="text-red-500 ml-1">*</span>}
      </label>

      <div ref={dropdownRef} className="relative">
        {selected ? (
          <div className="flex items-center gap-3 border-2 rounded-lg px-3 py-2.5 bg-blue-50 border-blue-300 transition-all">
            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center flex-shrink-0 text-white text-xs font-bold">
              {(selected.branch_name || "B")[0]?.toUpperCase?.() || "B"}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-gray-800 truncate">
                {selected.branch_name}
                {selected.branch_code && (
                  <span className="ml-2 text-xs font-mono text-blue-600 bg-blue-100 px-1.5 py-0.5 rounded">
                    {selected.branch_code}
                  </span>
                )}
              </p>
              <p className="text-xs text-gray-500 truncate">
                Branch ID: <span className="font-mono">{selected.id}</span>
              </p>
            </div>
            <button
              type="button"
              onClick={handleClear}
              className="flex-shrink-0 w-6 h-6 rounded-full bg-gray-200 hover:bg-red-100 hover:text-red-600 flex items-center justify-center text-gray-500 transition-colors"
              title="Change branch"
            >
              <FaTimes className="text-xs" />
            </button>
          </div>
        ) : (
          <button
            type="button"
            onClick={handleOpen}
            disabled={isDisabled}
            className={`w-full flex items-center justify-between border ${
              error ? "border-red-500 bg-red-50" : "border-gray-300"
            } px-3 py-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-300 transition text-sm ${
              isDisabled ? "bg-gray-100 text-gray-400 cursor-not-allowed" : "bg-white"
            }`}
            title={!bankId ? "Select a bank first" : ""}
          >
            <div className="flex items-center gap-2">
              <FaUser className="text-gray-400 text-sm" />
              <span>{!bankId ? "Select bank first" : "Select branch"}</span>
            </div>
            <div className="flex items-center gap-2">
              {loading ? (
                <svg className="animate-spin h-4 w-4 text-blue-400" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                </svg>
              ) : (
                <FaChevronDown className="text-gray-400 text-xs" />
              )}
            </div>
          </button>
        )}

        <AnimatePresence>
          {open && !selected && !isDisabled && (
            <motion.div
              initial={{ opacity: 0, y: -6, scaleY: 0.95 }}
              animate={{ opacity: 1, y: 0, scaleY: 1 }}
              exit={{ opacity: 0, y: -6, scaleY: 0.95 }}
              transition={{ duration: 0.15 }}
              className="absolute z-50 top-full mt-1 w-full bg-white border border-gray-200 rounded-xl shadow-xl overflow-hidden"
              style={{ transformOrigin: "top" }}
            >
              <div className="p-3 border-b border-gray-100 bg-white">
                <div className="relative">
                  <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm pointer-events-none" />
                  <input
                    type="text"
                    value={query}
                    onChange={handleSearchChange}
                    placeholder="Search branch name / code…"
                    className="w-full border border-gray-300 pl-9 pr-3 py-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-300 transition text-sm"
                  />
                </div>
              </div>

              {loading && results.length === 0 ? (
                <div className="flex items-center gap-2 px-4 py-3 text-sm text-gray-400">
                  <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                  </svg>
                  Loading branches…
                </div>
              ) : results.length === 0 ? (
                <div className="px-4 py-3 text-sm text-gray-400 flex items-center gap-2">
                  <FaUser className="text-gray-300" />
                  No branches found
                </div>
              ) : (
                <div className="max-h-64 overflow-y-auto divide-y divide-gray-50">
                  {results.map((branch) => (
                    <button
                      key={branch.id}
                      type="button"
                      onClick={() => handleSelect(branch)}
                      className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-blue-50 transition-colors text-left group"
                    >
                      <div className="w-8 h-8 rounded-full bg-gradient-to-br from-slate-400 to-slate-600 group-hover:from-blue-500 group-hover:to-indigo-600 flex items-center justify-center flex-shrink-0 text-white text-xs font-bold transition-all">
                        {(branch.branch_name || "B")[0]?.toUpperCase?.() || "B"}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-sm font-semibold text-gray-800 truncate">{branch.branch_name}</span>
                          {branch.branch_code && (
                            <span className="text-xs font-mono text-blue-600 bg-blue-50 px-1.5 py-0.5 rounded flex-shrink-0">
                              {branch.branch_code}
                            </span>
                          )}
                        </div>
                        <p className="text-xs text-gray-400 truncate mt-0.5">
                          Branch ID: <span className="font-mono">{branch.id}</span>
                        </p>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {error && <p className="text-red-500 text-sm mt-1">{error}</p>}
    </div>
  );
};

// ─── Step 1: Loan Details Form ────────────────────────────────────────────────
const LoanDetailsForm = ({ onNext }) => {
  const API_URL = process.env.REACT_APP_FRONTEND_URL;

  // ✅ FIX: stable emptyForm so useEffect deps are correct
  const emptyForm = useMemo(
    () => ({
      employee_no: "",
      loan_reference_no: "",
      loan_amount: "",
      loan_start_date: "",
      loan_end_date: "",
      loan_bank_id: "",
      loan_branch_id: "",
    }),
    []
  );

  const [formData, setFormData] = useState(emptyForm);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const cleared = sessionStorage.getItem("reimbursement_form_cleared");
    if (cleared === "true") {
      setFormData(emptyForm);
      sessionStorage.removeItem("reimbursement_form_cleared");
    }
  }, [emptyForm]);

  useEffect(() => {
    const handlePopState = () => {
      setFormData(emptyForm);
      setErrors({});
      sessionStorage.setItem("reimbursement_form_cleared", "true");
    };
    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, [emptyForm]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: null }));
  };

  const handleEmployeeSelect = (empNo) => {
    setFormData((prev) => ({ ...prev, employee_no: empNo }));
    setErrors((prev) => ({ ...prev, employee_no: null }));
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.employee_no.trim()) newErrors.employee_no = "Employee No is required";
    if (!formData.loan_reference_no.trim()) newErrors.loan_reference_no = "Loan Reference No is required";
    if (!formData.loan_amount || isNaN(formData.loan_amount) || Number(formData.loan_amount) <= 0)
      newErrors.loan_amount = "Enter a valid loan amount";
    if (!formData.loan_start_date) newErrors.loan_start_date = "Loan Start Date is required";
    if (!formData.loan_end_date) newErrors.loan_end_date = "Loan End Date is required";
    if (formData.loan_start_date && formData.loan_end_date && formData.loan_end_date <= formData.loan_start_date)
      newErrors.loan_end_date = "End date must be after start date";
    if (!formData.loan_bank_id || isNaN(formData.loan_bank_id)) newErrors.loan_bank_id = "Bank ID is required";
    if (!formData.loan_branch_id || isNaN(formData.loan_branch_id)) newErrors.loan_branch_id = "Branch ID is required";
    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      toast.error("Please fill all required fields correctly.");
      return;
    }

    setLoading(true);
    try {
      const token = Cookies.get("user_token");
      const payload = {
        ...formData,
        loan_amount: Number(formData.loan_amount),
        loan_bank_id: Number(formData.loan_bank_id),
        loan_branch_id: Number(formData.loan_branch_id),
      };

      const url = joinUrl(API_URL, "v1/hris/BankLoanReimbursement");

      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      const result = await response.json().catch(() => ({}));

      if (result.success || response.ok) {
        toast.success(result.message || "Reimbursement created successfully!");

        const returnedId =
          result?.data?.id || result?.data?.reimbursement_id || result?.id || result?.reimbursement_id || null;

        if (!returnedId) console.warn("⚠️ No reimbursement ID returned from backend:", result);

        setTimeout(() => onNext({ ...(result.data || payload), _recordId: returnedId }), 1000);
      } else {
        if (result.field) setErrors({ [result.field]: result.message });
        toast.error(result.message || "Something went wrong.");
      }
    } catch (error) {
      console.error("Submission error:", error);
      toast.error("Submission failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <form noValidate onSubmit={handleSubmit}>
      <h2 className="text-xl font-semibold text-gray-800 mb-1">Loan Information</h2>
      <p className="text-gray-500 mb-6 text-sm">Fill in the bank loan reimbursement details below.</p>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        <EmployeeSearchDropdown
          value={formData.employee_no}
          onSelect={(empNo) => handleEmployeeSelect(empNo)}
          error={errors.employee_no}
          required
        />

        <InputField
          label="Loan Reference No"
          name="loan_reference_no"
          value={formData.loan_reference_no}
          onChange={handleChange}
          error={errors.loan_reference_no}
          required
          placeholder="e.g. CB-ACC-88991"
        />
        <InputField
          label="Loan Amount"
          name="loan_amount"
          value={formData.loan_amount}
          onChange={handleChange}
          type="number"
          error={errors.loan_amount}
          required
          placeholder="e.g. 1540000"
        />
        <InputField
          label="Loan Start Date"
          name="loan_start_date"
          value={formData.loan_start_date}
          onChange={handleChange}
          type="date"
          error={errors.loan_start_date}
          required
        />
        <InputField
          label="Loan End Date"
          name="loan_end_date"
          value={formData.loan_end_date}
          onChange={handleChange}
          type="date"
          error={errors.loan_end_date}
          required
        />

        <BankSearchDropdown
          value={formData.loan_bank_id}
          onSelect={(bankId, bankObj) => {
            setFormData((prev) => ({
              ...prev,
              loan_bank_id: bankId,
              loan_branch_id: "",     // ✅ clear branch
            }));
            setErrors((prev) => ({ ...prev, loan_bank_id: null, loan_branch_id: null }));
          }}
          error={errors.loan_bank_id}
          required
        />

        <BranchSearchDropdown
          bankId={formData.loan_bank_id}
          value={formData.loan_branch_id}
          onSelect={(branchId, branchObj) => {
            setFormData((prev) => ({ ...prev, loan_branch_id: branchId }));
            setErrors((prev) => ({ ...prev, loan_branch_id: null }));
          }}
          error={errors.loan_branch_id}
          required
          disabled={!formData.loan_bank_id}
        />
      </div>

      <div className="mt-8 flex justify-end">
        <button
          type="submit"
          disabled={loading}
          className={`bg-blue-600 text-white px-6 py-3 rounded-lg flex items-center gap-2 hover:bg-blue-700 transition-all ${
            loading ? "opacity-60 cursor-not-allowed" : ""
          }`}
        >
          {loading ? (
            <>
              <svg className="animate-spin h-5 w-5 text-white" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
              </svg>
              Submitting...
            </>
          ) : (
            <>
              Next <FaArrowRight />
            </>
          )}
        </button>
      </div>
    </form>
  );
};

// ─── Success Modal ────────────────────────────────────────────────────────────
const SuccessModal = ({ isOpen, onClose, onDone, loanRef, rowCount }) => (
  <AnimatePresence>
    {isOpen && (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/40 backdrop-blur-sm"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.75, opacity: 0, y: 40 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          exit={{ scale: 0.75, opacity: 0, y: 40 }}
          transition={{ type: "spring", stiffness: 300, damping: 24 }}
          className="bg-white rounded-3xl shadow-2xl p-10 max-w-md w-full mx-4 text-center relative"
          onClick={(e) => e.stopPropagation()}
        >
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ type: "spring", stiffness: 260, damping: 20, delay: 0.1 }}
            className="w-20 h-20 bg-gradient-to-br from-green-400 to-emerald-500 rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg shadow-green-200"
          >
            <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <motion.path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2.5}
                d="M5 13l4 4L19 7"
                initial={{ pathLength: 0 }}
                animate={{ pathLength: 1 }}
                transition={{ duration: 0.5, delay: 0.2 }}
              />
            </svg>
          </motion.div>

          <motion.h2
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.25 }}
            className="text-2xl font-bold text-gray-800 mb-2"
          >
            Import Successful!
          </motion.h2>

          <motion.p
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.32 }}
            className="text-gray-500 text-sm mb-1"
          >
            Loan schedule for
          </motion.p>

          <motion.p
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.38 }}
            className="text-blue-600 font-semibold text-base mb-4"
          >
            {loanRef || "—"}
          </motion.p>

          {rowCount > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.44 }}
              className="inline-flex items-center gap-2 bg-green-50 border border-green-200 text-green-700 px-4 py-2 rounded-full text-sm font-medium mb-6"
            >
              {rowCount} installment{rowCount !== 1 ? "s" : ""} imported
            </motion.div>
          )}

          <motion.button
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            onClick={onDone}
            className="w-full bg-gradient-to-r from-green-500 to-emerald-600 text-white font-semibold py-3 rounded-xl hover:from-green-600 hover:to-emerald-700 transition-all duration-300 shadow-lg shadow-green-200 hover:shadow-green-300"
          >
            Done
          </motion.button>
        </motion.div>
      </motion.div>
    )}
  </AnimatePresence>
);

// ─── Dropdown options (ONLY required fields) ───────────────────────────────────
const REQUIRED_BACKEND_COLUMNS = [
  { value: "", label: "— Ignore —" },
  { value: "installment_no", label: "Installment No" },
  { value: "installment_date", label: "Installment Date" },
  { value: "interest_amount", label: "Interest Amount" },
];

// ─── Confirm Import Modal ──────────────────────────────────────────────────────
const ConfirmImportModal = ({ isOpen, onClose, onConfirm, importing, missingCols = [], fileName }) => (
  <AnimatePresence>
    {isOpen && (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/40 backdrop-blur-sm"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.95, opacity: 0, y: 10 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          exit={{ scale: 0.95, opacity: 0, y: 10 }}
          transition={{ type: "spring", stiffness: 260, damping: 22 }}
          className="bg-white rounded-2xl shadow-2xl max-w-lg w-full mx-4 p-6 relative"
          onClick={(e) => e.stopPropagation()}
        >
          <h3 className="text-lg font-semibold text-slate-800">Confirm Import</h3>
          <p className="text-sm text-slate-500 mt-1">
            You’re about to import the schedule from{" "}
            <span className="font-medium text-indigo-600">{fileName || "your Excel file"}</span>.
          </p>

          {missingCols.length > 0 && (
            <div className="mt-4 rounded-xl border border-red-200 bg-red-50 p-3">
              <p className="text-sm font-medium text-red-700">Missing required mappings:</p>
              <ul className="mt-1 text-sm text-red-600 list-disc pl-5">
                {missingCols.map((c) => (
                  <li key={c}>{c}</li>
                ))}
              </ul>
            </div>
          )}

          <div className="mt-6 flex items-center justify-end gap-3">
            <button
              onClick={onClose}
              disabled={importing}
              className={`px-4 py-2 rounded-lg text-sm border border-slate-300 text-slate-600 hover:bg-slate-50 transition ${
                importing ? "opacity-60 cursor-not-allowed" : ""
              }`}
            >
              Cancel
            </button>

            <button
              onClick={onConfirm}
              disabled={importing || missingCols.length > 0}
              className={`px-4 py-2 rounded-lg text-sm text-white bg-indigo-600 hover:bg-indigo-700 transition flex items-center gap-2 ${
                importing || missingCols.length > 0 ? "opacity-60 cursor-not-allowed" : ""
              }`}
            >
              {importing ? "Importing..." : "Yes, Import"}
            </button>
          </div>
        </motion.div>
      </motion.div>
    )}
  </AnimatePresence>
);

// ─── Step 2: Excel Upload + Mapping + Preview ──────────────────────────────────
// NOTE: Your Step2 stays EXACTLY the same — pasted as-is below
// ─── Step 2: Excel Upload + Mapping + Preview ──────────────────────────────────
const Step2 = ({ submittedData }) => {
  const navigate = useNavigate();
  const API_URL = process.env.REACT_APP_FRONTEND_URL;

  const recordId = submittedData?._recordId;

  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);

  const [tempKey, setTempKey] = useState(null);
  const [mapping, setMapping] = useState({});
  const [excelHeaders, setExcelHeaders] = useState([]);

  // ✅ store ALL rows for preview (mapping stage)
  const [previewRows, setPreviewRows] = useState([]);

  const [previewData, setPreviewData] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importDone, setImportDone] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);

  const [visibleCount, setVisibleCount] = useState(200);
  const visibleRows = useMemo(() => previewRows.slice(0, visibleCount), [previewRows, visibleCount]);

  // ✅ correct row count for modal
  const [importedCount, setImportedCount] = useState(0);

  const [showConfirmImport, setShowConfirmImport] = useState(false);
  const [validationErrors, setValidationErrors] = useState(null);

  const MANDATORY_COLS = ["installment_no", "installment_date", "interest_amount"];

  const getMissingMappings = () => {
    const finalMapping = {};
    Object.entries(mapping).forEach(([excelHeader, backendCol]) => {
      if (backendCol) finalMapping[backendCol] = excelHeader;
    });
    return MANDATORY_COLS.filter((c) => !finalMapping[c]);
  };

  const handleMappingChange = (excelHeader, backendCol) => {
    setMapping((prev) => {
      const updated = { ...prev };

      // prevent duplicates: one backend field can be mapped once
      if (backendCol !== "") {
        Object.keys(updated).forEach((key) => {
          if (updated[key] === backendCol) delete updated[key];
        });
        updated[excelHeader] = backendCol;
      } else {
        delete updated[excelHeader];
      }

      return updated;
    });
  };

  const getCurrentBackendColFor = (excelHeader) => mapping[excelHeader] || "";

  const handleUpload = async (selectedFile) => {
    if (!selectedFile) return;

    if (!recordId) {
      toast.error("Cannot upload: Reimbursement ID is missing. Please restart the form.");
      console.error("❌ recordId is undefined. Step1 may not return ID correctly.");
      return;
    }

    const ext = selectedFile.name.split(".").pop().toLowerCase();
    if (!["xlsx", "xls"].includes(ext)) {
      toast.error("Only Excel files (.xlsx, .xls) are allowed.");
      return;
    }

    setUploading(true);

    try {
      const token = Cookies.get("user_token");
      const formData = new FormData();
      formData.append("schedule", selectedFile);

      const res = await fetch(
        `${API_URL}/v1/hris/BankLoanReimbursement/${recordId}/schedule/preview`,
        {
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
          body: formData,
        }
      );

      const result = await res.json().catch(() => ({}));

      if (!res.ok || result.error) {
        toast.error(result.message || result.error || "Upload failed.");
        return;
      }

      setTempKey(result.temp_key);

      const headers = result.preview?.headers || [];
      setExcelHeaders(headers);

      // ✅ IMPORTANT:
      // Your backend must send all rows in preview for "all rows preview"
      // Try these common keys:
      const rows =
        result.preview?.rows ||
        result.preview?.allRows ||
        result.preview?.data ||
        result.preview?.sampleRows || // fallback if backend only provides samples
        [];

      setPreviewRows(Array.isArray(rows) ? rows : []);
      console.log("previewRows:", rows?.length);

      // reset mapping
      setMapping({});
      toast.success("File uploaded! Review and confirm column mapping.");
    } catch (err) {
      console.error(err);
      toast.error("Upload failed. Please try again.");
    } finally {
      setUploading(false);
    }
  };

  const onFileChange = (e) => {
    const f = e.target.files?.[0];
    if (f) {
      setFile(f);
      handleUpload(f);
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const f = e.dataTransfer.files?.[0];
    if (f) {
      setFile(f);
      handleUpload(f);
    }
  };

  const handleImport = async () => {
    const finalMapping = {};
    Object.entries(mapping).forEach(([excelHeader, backendCol]) => {
      if (backendCol) finalMapping[backendCol] = excelHeader;
    });

    const missing = MANDATORY_COLS.filter((c) => !finalMapping[c]);
    if (missing.length > 0) {
      toast.error(`Please map these required columns: ${missing.join(", ")}`);
      return;
    }

    setImporting(true);

    try {
      const token = Cookies.get("user_token");

      const res = await fetch(
        `${API_URL}/v1/hris/BankLoanReimbursement/${recordId}/schedule/import`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ temp_key: tempKey, mapping: finalMapping }),
        }
      );

      const result = await res.json().catch(() => ({}));

      if (!res.ok || !result.success) {
        if (result.errorRows && result.errorRows.length > 0) {
          setValidationErrors(result);
          toast.error(result.message || "Validation errors found in your file.");
        } else {
          toast.error(result.message || result.error || "Import failed.");
        }
        return;
      }

      // rows returned from API
      const rawRows =
        result.data ??
        result.rows ??
        result.schedule ??
        result.installments ??
        result.preview ??
        [];

      const rows = Array.isArray(rawRows)
        ? rawRows
        : typeof rawRows === "object" && rawRows !== null
        ? Object.values(rawRows)
        : [];

      // ✅ accurate count: prefer API count if exists, else fallback to rows.length
      const countFromApi =
        result?.data?.imported_count ??
        result?.data?.count ??
        result?.imported_count ??
        result?.count ??
        result?.rows_count ??
        null;

      const count = Number.isFinite(Number(countFromApi))
        ? Number(countFromApi)
        : rows.length;

      setImportedCount(count);

      const PREVIEW_HEADERS = [
        "installment_no",
        "installment_date",
        "interest_amount",
        "principal_amount",
        "total_installment_amount",
      ];

      setPreviewData({ headers: PREVIEW_HEADERS, rows });
      setImportDone(true);
      setShowSuccessModal(true);
    } catch (err) {
      console.error(err);
      toast.error("Import failed. Please try again.");
    } finally {
      setImporting(false);
    }
  };

  // ─── Imported schedule preview (after import) ───────────────────────────────
  if (importDone && previewData) {
    return (
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}>
        <div className="flex items-center gap-2 mb-2">
          <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
            <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h2 className="text-xl font-semibold text-gray-800">Schedule Preview</h2>
        </div>

        <p className="text-gray-500 text-sm mb-5">
          Loan <span className="font-medium text-blue-600">{submittedData?.loan_reference_no}</span> —{" "}
          {importedCount} installments imported.
        </p>

        {/* fixed header + scroll body */}
        <div className="rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm table-fixed">
              <thead className="sticky top-0 z-10">
                <tr className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white">
                  {previewData.headers.map((h) => (
                    <th
                      key={h}
                      className="px-4 py-3 text-left font-semibold whitespace-nowrap"
                      style={{ width: `${100 / previewData.headers.length}%` }}
                    >
                      {h.replaceAll("_", " ").replace(/\b\w/g, (l) => l.toUpperCase())}
                    </th>
                  ))}
                </tr>
              </thead>
            </table>
          </div>

          <div className="overflow-y-auto overflow-x-auto" style={{ maxHeight: "420px" }}>
            <table className="min-w-full text-sm table-fixed">
              <tbody>
                {previewData.rows.map((row, i) => (
                  <tr key={i} className={i % 2 === 0 ? "bg-white" : "bg-slate-50"}>
                    {previewData.headers.map((h) => {
                      const v = row?.[h];
                      const display = v === null || v === undefined ? "" : String(v);
                      return (
                        <td
                          key={h}
                          className="px-4 py-2.5 text-gray-700 border-b border-gray-100 whitespace-nowrap truncate"
                          title={display}
                        >
                          {display}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <SuccessModal
          isOpen={showSuccessModal}
          onClose={() => setShowSuccessModal(false)}
          onDone={() => navigate("/bank-loan-reimbursement")}
          loanRef={submittedData?.loan_reference_no}
          rowCount={importedCount} // ✅ FIXED
        />
      </motion.div>
    );
  }

  // ─── Validation Error UI ────────────────────────────────────────────────────
  if (validationErrors) {
    return (
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}>
        <div className="flex items-center gap-3 mb-4">
          <div className="w-9 h-9 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
            <svg className="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
            </svg>
          </div>
          <div>
            <h2 className="text-xl font-semibold text-gray-800">Validation Errors Found</h2>
            <p className="text-sm text-gray-500">{validationErrors.message}</p>
          </div>
          <span className="ml-auto bg-red-100 text-red-700 text-xs font-bold px-3 py-1 rounded-full">
            {validationErrors.errorCount} error{validationErrors.errorCount !== 1 ? "s" : ""}
          </span>
        </div>

        <div className="rounded-xl border border-red-200 overflow-hidden shadow-sm">
          <div className="overflow-x-auto overflow-y-auto" style={{ maxHeight: "460px" }}>
            <table className="min-w-full text-sm">
              <thead className="sticky top-0 z-10 bg-red-600 text-white">
                <tr>
                  <th className="px-4 py-3 text-left font-semibold whitespace-nowrap w-20">Row</th>
                  <th className="px-4 py-3 text-left font-semibold whitespace-nowrap w-64">Issues</th>
                  <th className="px-4 py-3 text-left font-semibold whitespace-nowrap">Row Data</th>
                </tr>
              </thead>
              <tbody>
                {validationErrors.errorRows.map((errRow, i) => (
                  <tr key={i} className={i % 2 === 0 ? "bg-white" : "bg-red-50"}>
                    <td className="px-4 py-3 font-mono font-bold text-red-600 align-top border-b border-red-100">
                      #{errRow.rowNo}
                    </td>
                    <td className="px-4 py-3 align-top border-b border-red-100">
                      <ul className="space-y-1">
                        {errRow.issues.map((issue, j) => (
                          <li key={j} className="flex items-start gap-1.5 text-red-700 text-xs">
                            <span className="mt-0.5 flex-shrink-0 w-3.5 h-3.5 bg-red-200 rounded-full flex items-center justify-center text-red-600 font-bold text-[10px]">!</span>
                            {issue}
                          </li>
                        ))}
                      </ul>
                    </td>
                    <td className="px-4 py-3 align-top border-b border-red-100">
                      <div className="flex flex-wrap gap-x-4 gap-y-1">
                        {Object.entries(errRow.rowData).map(([key, val]) => (
                          <span key={key} className="text-xs text-gray-500">
                            <span className="font-medium text-gray-700">{key}:</span>{" "}
                            <span className="font-mono">{val === null || val === undefined ? "—" : String(val)}</span>
                          </span>
                        ))}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="mt-5 flex justify-between items-center">
          <p className="text-sm text-gray-500">Fix the highlighted rows in your Excel file and re-upload.</p>
          <button
            onClick={() => {
              setValidationErrors(null);
              setTempKey(null);
              setExcelHeaders([]);
              setMapping({});
              setPreviewRows([]);
              setFile(null);
            }}
            className="bg-blue-600 text-white px-5 py-2.5 rounded-lg text-sm hover:bg-blue-700 transition-all flex items-center gap-2"
          >
            ← Re-upload File
          </button>
        </div>
      </motion.div>
    );
  }

  // ─── Mapping UI ─────────────────────────────────────────────────────────────
  if (tempKey && excelHeaders.length > 0) {
    const REQUIRED_COLS = ["installment_no", "installment_date", "interest_amount"];

    const chunked = [];
    for (let i = 0; i < excelHeaders.length; i += 4) chunked.push(excelHeaders.slice(i, i + 4));

    return (
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}>
        <div className="bg-slate-50 border border-slate-200 rounded-2xl p-6">
          <h2 className="text-xl font-semibold text-slate-800 mb-1">Map Excel Columns</h2>
          <p className="text-slate-500 text-sm mb-6">
            Detected <span className="font-medium text-indigo-600">{excelHeaders.length}</span> columns from{" "}
            <span className="font-medium text-indigo-600">{file?.name}</span>. Map only the required fields.
          </p>

          <div className="space-y-4">
            {chunked.map((row, rowIndex) => (
              <div key={rowIndex} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {row.map((header) => (
                  <div key={header}>
                    <div className="mb-3">
                      <p className="text-[13px] font-semibold text-slate-700 truncate">{header}</p>
                      <div className="mt-1 h-[2px] w-10 bg-indigo-200 rounded-full" />
                    </div>

                    <select
                      value={getCurrentBackendColFor(header)}
                      onChange={(e) => handleMappingChange(header, e.target.value)}
                      className="w-full border border-slate-300 px-3 py-2 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-300 bg-white"
                    >
                      {REQUIRED_BACKEND_COLUMNS.map((col) => (
                        <option
                          key={col.value}
                          value={col.value}
                          disabled={
                            col.value !== "" &&
                            col.value !== getCurrentBackendColFor(header) &&
                            Object.values(mapping).includes(col.value)
                          }
                        >
                          {col.label}
                        </option>
                      ))}
                    </select>
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>

        {/* required chips */}
        <div className="flex flex-wrap gap-2 mb-6 mt-5">
          {REQUIRED_COLS.map((col) => {
            const mapped = Object.entries(mapping).find(([, v]) => v === col);
            return (
              <span
                key={col}
                className={`px-3 py-1 rounded-full text-xs font-medium ${
                  mapped ? "bg-green-100 text-green-700" : "bg-red-50 text-red-500 border border-red-200"
                }`}
              >
                {mapped ? "✓" : "⚠ required"} {col}
                {mapped ? ` ← "${mapped[0]}"` : ""}
              </span>
            );
          })}
        </div>

        {/* ✅ ALL ROWS PREVIEW (no placeholders) */}
        {previewRows.length > 0 && (
          <div className="mb-6">
            <p className="text-xs text-gray-500 font-medium mb-2">
              📋 Preview from your file (all rows, scroll):
              <span className="ml-2 text-gray-400">({previewRows.length} rows)</span>
            </p>

            <div className="rounded-xl border border-gray-200 shadow-sm overflow-hidden">
              {/* header */}
              <div className="overflow-x-auto">
                <table className="min-w-full text-xs table-fixed">
                  <thead className="sticky top-0 z-10">
                    <tr className="bg-slate-100">
                      {excelHeaders.map((h) => (
                        <th
                          key={h}
                          className="px-3 py-2 text-left font-semibold text-gray-600 whitespace-nowrap border-b border-gray-200"
                        >
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                </table>
              </div>

              {/* body: fixed height (~10 rows) + scroll */}
              <div className="overflow-x-auto overflow-y-auto" style={{ maxHeight: "420px" }}>
                <table className="min-w-full text-xs table-fixed">
                  <tbody>
                    {visibleRows.map((row, i) => (
                      <tr key={i} className={i % 2 === 0 ? "bg-white" : "bg-slate-50"}>
                        {excelHeaders.map((h) => {
                          const v = row?.[h];
                          const display = v === null || v === undefined ? "" : String(v);
                          return (
                            <td
                              key={h}
                              className="px-3 py-2 text-gray-600 border-b border-gray-100 whitespace-nowrap"
                              title={display}
                            >
                              {display}
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {previewRows.length > visibleCount && (
              <div className="mt-3 flex justify-center">
                <button
                  type="button"
                  onClick={() => setVisibleCount((v) => v + 200)}
                  className="px-4 py-2 rounded-lg bg-slate-100 hover:bg-slate-200 text-sm text-slate-700"
                >
                  Load more ({Math.min(200, previewRows.length - visibleCount)} rows)
                </button>
              </div>
            )}
          </div>
        )}

        <div className="flex justify-between items-center">
          <button
            className="text-sm text-gray-500 underline hover:text-gray-700"
            onClick={() => {
              setTempKey(null);
              setExcelHeaders([]);
              setMapping({});
              setPreviewRows([]);
              setFile(null);
            }}
          >
            ← Re-upload file
          </button>

          <button
            onClick={() => setShowConfirmImport(true)}
            disabled={importing}
            className={`bg-blue-600 text-white px-6 py-3 rounded-lg flex items-center gap-2 hover:bg-blue-700 transition-all ${
              importing ? "opacity-60 cursor-not-allowed" : ""
            }`}
          >
            {importing ? (
              <>
                <svg className="animate-spin h-4 w-4 text-white" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                </svg>
                Importing...
              </>
            ) : (
              <>
                Confirm & Import <FaArrowRight />
              </>
            )}
          </button>
        </div>

        <ConfirmImportModal
          isOpen={showConfirmImport}
          onClose={() => setShowConfirmImport(false)}
          onConfirm={() => {
            setShowConfirmImport(false);
            handleImport();
          }}
          importing={importing}
          missingCols={getMissingMappings()}
          fileName={file?.name}
        />
      </motion.div>
    );
  }

  // ─── Upload UI ──────────────────────────────────────────────────────────────
  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}>
      <div className="flex items-center gap-2 mb-1">
        <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
          <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        </div>
        <h2 className="text-xl font-semibold text-gray-800">Upload Loan Schedule</h2>
      </div>

      <p className="text-gray-500 text-sm mb-6">
        Loan reference <span className="font-medium text-blue-600">{submittedData?.loan_reference_no}</span> created. Now
        upload the repayment schedule Excel file.
      </p>

      <div
        onDragOver={(e) => {
          e.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
        className={`border-2 border-dashed rounded-2xl p-12 text-center transition-all duration-300 cursor-pointer ${
          dragOver ? "border-blue-500 bg-blue-50" : "border-gray-300 bg-gray-50 hover:border-blue-400 hover:bg-blue-50/50"
        }`}
        onClick={() => document.getElementById("excel-upload-input").click()}
      >
        {uploading ? (
          <div className="flex flex-col items-center gap-3">
            <svg className="animate-spin h-10 w-10 text-blue-500" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
            </svg>
            <p className="text-blue-600 font-medium">Uploading & analyzing...</p>
          </div>
        ) : (
          <>
            <div className="w-14 h-14 bg-blue-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <svg className="w-7 h-7 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
              </svg>
            </div>
            <p className="text-gray-700 font-medium mb-1">{dragOver ? "Drop your file here" : "Drag & drop your Excel file here"}</p>
            <p className="text-gray-400 text-sm mb-3">or click to browse</p>
            <span className="text-xs bg-gray-200 text-gray-600 px-3 py-1 rounded-full">.xlsx / .xls only</span>
          </>
        )}

        <input id="excel-upload-input" type="file" accept=".xlsx,.xls" className="hidden" onChange={onFileChange} />
      </div>
    </motion.div>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────
const STEPS = ["Loan Details", "Confirmation"];

const CreateReimbursement = () => {
  const [currentStep, setCurrentStep] = useState(1);
  const [submittedData, setSubmittedData] = useState(null);

  const handleNext = (data) => {
    setSubmittedData(data);
    setCurrentStep(2);
  };

  return (
    <motion.div
      initial={{ opacity: 0, x: 50 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -50 }}
      transition={{ duration: 0.4 }}
    >
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 p-8 font-montserrat">
        <div className="mb-8 flex items-center gap-3">
          <div className="bg-gradient-to-br from-green-200 to-green-500 p-2 rounded-xl">
            <MdOutlineAddCard className="text-white text-2xl" />
          </div>
          <div>
            <h1 className="text-[25px] font-semibold text-slate-800">Create Reimbursement</h1>
            <p className="text-gray-500 text-sm">Bank loan reimbursement registration</p>
          </div>
        </div>

        <div className="bg-white shadow-md rounded-2xl p-8 w-full">
          <StepIndicator currentStep={currentStep} steps={STEPS} />

          <AnimatePresence mode="wait">
            {currentStep === 1 && (
              <motion.div
                key="step1"
                initial={{ opacity: 0, x: 40 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -40 }}
                transition={{ duration: 0.3 }}
              >
                <LoanDetailsForm onNext={handleNext} />
              </motion.div>
            )}

            {currentStep === 2 && (
              <motion.div
                key="step2"
                initial={{ opacity: 0, x: 40 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -40 }}
                transition={{ duration: 0.3 }}
              >
                <Step2 submittedData={submittedData} />
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>

      <ToastContainer position="top-right" autoClose={3000} hideProgressBar />
    </motion.div>
  );
};

export default CreateReimbursement;